import { useEffect, useRef } from 'react';
import { useSocketConnection } from '../../hooks/useSocketConnection';
import { globalNotificationService } from '../../services/global-notification.service';
import { GlobalSocketService } from '../../services/socket.service';
import { useGuestStore, useUserProfileStore } from '../../stores';
import { useGuestWorkplaceStore } from '../../guest/stores/guest-workplace.store';
import { WorkplaceEventRouter } from '../../workplace/events/WorkplaceEventRouter';
import { refreshEntitlements, useEntitlementsStore } from '@/shared/stores/entitlements.store';

export function useAppBootstrap() {
  const bootstrappedKeyRef = useRef<string | null>(null);
  const userProfile = useUserProfileStore((s) => s.profile);
  const guestInfo = useGuestStore((s) => s.guest);
  const guestOrgPid = useGuestWorkplaceStore((s) => s.org.pid);
  const { isConnected, isInitialized } = useSocketConnection();

  // Derive actor identity — supports both regular users and guest sessions
  const actorPid = userProfile?.pid ?? guestInfo?.pid;
  const orgPid = userProfile?.org_pid ?? guestOrgPid;

  useEffect(() => {
    if (!isConnected || !isInitialized || !actorPid || !orgPid) return;

    const bootstrapKey = `${actorPid}:${orgPid}`;
    if (bootstrappedKeyRef.current === bootstrapKey) return;

    bootstrappedKeyRef.current = bootstrapKey;

    let cancelled = false;

    // Request subscription init before registering listeners — avoids arbitrary delays
    // but does not guarantee the backend has fully applied subscriptions yet.
    GlobalSocketService.forceReinitializeSubscriptions().then((success) => {
      if (cancelled) return;
      if (!success) {
        bootstrappedKeyRef.current = null; // allow retry on next render
        return;
      }
      WorkplaceEventRouter.registerEventListeners();
      globalNotificationService.initialize();
    });

    return () => {
      cancelled = true;
      WorkplaceEventRouter.cleanup();
      globalNotificationService.destroy();
      bootstrappedKeyRef.current = null;
    };
  }, [isConnected, isInitialized, actorPid, orgPid]);

  // Re-register listeners when tab regains focus
  useEffect(() => {
    let cancelled = false;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isConnected && isInitialized) {
        GlobalSocketService.forceReinitializeSubscriptions().then((success) => {
          if (!cancelled && success) {
            WorkplaceEventRouter.registerEventListeners();
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isConnected, isInitialized]);

  // Fetch entitlements once the org context is ready. Guest-inclusive: guests get
  // the visited org's set, which the former feature gates (hasFeature) read.
  useEffect(() => {
    if (orgPid) {
      useEntitlementsStore.getState().reset();
      useEntitlementsStore.getState().fetchEntitlements();
    }
  }, [orgPid]);

  // Pick up plan changes without a reload: refetch when the tab comes back into
  // view (at most once a minute) and every 5 minutes while it's open.
  useEffect(() => {
    if (!userProfile?.org_pid) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshEntitlements(60_000);
    };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(() => void refreshEntitlements(), 5 * 60_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [userProfile?.org_pid]);
}
